import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';

export default function DigitalTestingACT() {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [userName, setUserName] = useState('');
  const [test, setTest] = useState([[], [], [], []]);
  const [showInstructions, setShowInstructions] = useState(false);
  const [showFormulas, setShowFormulas] = useState(false);
  const [showFRQInstructions, setShowFRQInstructions] = useState(false);
  const [testId, setTestId] = useState('');
  const [qIndex, setQIndex] = useState(-1);
  const [studentAnswers, setStudentAnswers] = useState([]);
  const [flaggedQuestions, setFlaggedQuestions] = useState([]);
  const [mIndex, setMIndex] = useState(-1);
  const [openConfirm, setOpenConfirm] = useState(false);
  const [hideStartButton, setHideStartButton] = useState(false);
  const [secret, setSecret] = useState('');
  const [countLeaves, setCountLeaves] = useState(0);
  const [isDrawing, setIsDrawing] = useState(false);
  const [englishTime, setEnglishTime] = useState(32 * 60);
  const [mathTime, setMathTime] = useState(35 * 60);
  const [breakTime, setBreakTime] = useState(5 * 60);
  const [timeRemaining, setTimeRemaining] = useState(0);
  const [showNavigation, setShowNavigation] = useState(true);
  const [leftPanelWidth, setLeftPanelWidth] = useState(50); // percentage
  const [isResizing, setIsResizing] = useState(false);

  const imageRef = useRef(null);
  const canvasRef = useRef(null);
  const contextRef = useRef(null);
  const containerRef = useRef(null);

  // Timer
  useEffect(() => {
    if (mIndex > -1 && !showInstructions) {
      const timer = setInterval(() => {
        setTimeRemaining((prev) => {
          if (prev <= 1) {
            clearInterval(timer);
            confirmSubmitModule();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [mIndex, showInstructions]);

  // Initialize timer when module/instructions change
  useEffect(() => {
    if (mIndex > -1) {
      if (!showInstructions) {
        setTimeRemaining(mIndex < 2 ? englishTime : mathTime);
      } else {
        setTimeRemaining(breakTime);
      }
    }
  }, [mIndex, showInstructions]);

  // Handle resize
  useEffect(() => {
    const handleMouseMove = (e) => {
      if (!isResizing || !containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      const newWidth = ((e.clientX - containerRect.left) / containerRect.width) * 100;
      if (newWidth > 20 && newWidth < 80) {
        setLeftPanelWidth(newWidth);
      }
    };

    const handleMouseUp = () => {
      setIsResizing(false);
    };

    if (isResizing) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isResizing]);

  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  const login = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('https://www.tenaflyprepacademy.com/server/login', {
        email: email,
        password: password,
      });
      localStorage.setItem('user', response.data);
      setSecret('');
      setIsLoggedIn(true);
      
      // Fetch user info
      try {
        const userResponse = await axios.get('https://www.tenaflyprepacademy.com/server/user', {
          headers: {
            JWT: 'Bearer ' + response.data,
          },
        });
        if (userResponse.data.user && userResponse.data.user.name) {
          setUserName(userResponse.data.user.name);
        }
      } catch (err) {
        console.log('Could not fetch user info:', err);
      }
    } catch (err) {
      console.log(err);
      alert('Login Failed');
    }
  };

  const changeCanvas = () => {
    if (imageRef.current !== null) {
      const canvas = canvasRef.current;
      canvas.width = imageRef.current.offsetWidth * 2;
      canvas.height = imageRef.current.offsetHeight * 2;
      canvas.style.width = imageRef.current.offsetWidth + 'px';
      canvas.style.height = imageRef.current.offsetHeight + 'px';

      const context = canvas.getContext('2d');
      context.scale(2, 2);
      context.strokeStyle = 'yellow';
      context.lineWidth = 15;
      context.globalAlpha = 0.6;
      context.globalCompositeOperation = 'xor';
      contextRef.current = context;
    }
  };

  const startDrawing = ({ nativeEvent }) => {
    if (!contextRef.current) return;
    contextRef.current.beginPath();
    if (nativeEvent.touches) {
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const canvasX = nativeEvent.changedTouches[0].clientX - canvasRect.left;
      const canvasY = nativeEvent.changedTouches[0].clientY - canvasRect.top;
      contextRef.current.moveTo(canvasX, canvasY);
    } else {
      const { offsetX, offsetY } = nativeEvent;
      contextRef.current.moveTo(offsetX, offsetY);
    }
    setIsDrawing(true);
  };

  const finishDrawing = () => {
    if (contextRef.current) {
      contextRef.current.closePath();
    }
    setIsDrawing(false);
  };

  const clearCanvas = () => {
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');
    context.clearRect(0, 0, canvas.width, canvas.height);
  };

  const draw = ({ nativeEvent }) => {
    if (!isDrawing) return;
    if (nativeEvent.touches) {
      const canvasRect = canvasRef.current.getBoundingClientRect();
      const canvasX = nativeEvent.changedTouches[0].clientX - canvasRect.left;
      const canvasY = nativeEvent.changedTouches[0].clientY - canvasRect.top;
      contextRef.current.lineTo(canvasX, canvasY);
    } else {
      const { offsetX, offsetY } = nativeEvent;
      contextRef.current.lineTo(offsetX, offsetY);
    }
    contextRef.current.stroke();
  };

  const alertUser = (e) => {
    e.preventDefault();
    e.returnValue =
      'Leaving this webpage will result in a faulty testing experience and will have a high chance to invalidate your score.';
  };

  const detectLeave = (e) => {
    e.preventDefault();
    if (mIndex > -1 && document.visibilityState !== 'visible') {
      console.log('left this page');
      setCountLeaves(countLeaves + 1);
      alert('You have left this testing page. If you continue to do so, your test may be invalidated.');
    }
  };

  useEffect(() => {
    window.addEventListener('beforeunload', alertUser);
    window.onpopstate = alertUser;
    return () => {
      window.removeEventListener('beforeunload', alertUser);
      window.onpopstate = null;
    };
  }, []);

  useEffect(() => {
    document.addEventListener('visibilitychange', detectLeave);
    return () => {
      document.removeEventListener('visibilitychange', detectLeave);
    };
  });

  useEffect(() => {
    setOpenConfirm(false);
  }, [qIndex, studentAnswers, mIndex]);

  const getTest = async (e) => {
    e.preventDefault();
    try {
      const response = await axios.post('https://www.tenaflyprepacademy.com/server/digitalSAT/verify', {
        JWT: localStorage.getItem('user'),
        secret_code: secret,
      });
      if (response.data === undefined || response.data === null || response.data === '') {
        alert('Test not found');
        return null;
      }
      if (test[0].length !== 0) return;

      try {
        const userResponse = await axios.get('https://www.tenaflyprepacademy.com/server/user', {
          headers: {
            JWT: 'Bearer ' + localStorage.getItem('user'),
          },
        });
        
        // Get user name
        if (userResponse.data.user && userResponse.data.user.name) {
          setUserName(userResponse.data.user.name);
        }
        
        const extraTime = parseFloat(userResponse.data.user.extra_time);
        if (extraTime && extraTime > 0) {
          setEnglishTime(englishTime * extraTime);
          setMathTime(mathTime * extraTime);
          setBreakTime(breakTime * extraTime);
        }
        setIsLoggedIn(true);
      } catch (err) {
        console.log(err);
        alert('Login Failed');
      }

      const questions = response.data.questions;
      const newTest = [[], [], [], []];
      for (let i = 0; i < questions.length; i++) {
        if (questions[i].mr === 'Reading') newTest[questions[i].module - 1].push(questions[i]);
        else if (questions[i].mr === 'Math') newTest[questions[i].module + 1].push(questions[i]);
      }
      for (let y = 0; y < 4; y++) {
        newTest[y].sort(function (a, b) {
          return a.number - b.number;
        });
      }
      setTest(newTest);

      const newAnswers = [];
      const newFlags = [];
      for (let x = 0; x < 4; x++) {
        const mod = [];
        const bools = [];
        for (let j = 0; j < newTest[x].length; j++) {
          mod.push('');
          bools.push(false);
        }
        newAnswers.push(mod);
        newFlags.push(bools);
      }
      setStudentAnswers(newAnswers);
      setFlaggedQuestions(newFlags);
      setQIndex(0);
      setMIndex(0);
      setTestId(response.data.test_id);
      setHideStartButton(true);
      setShowInstructions(true);
      return response.data;
    } catch {
      alert('No server response or your session has expired.');
    }
  };

  const submitForGrading = async () => {
    const allStudentAnswers = [];
    for (let mi = 0; mi < 4; mi++) {
      for (let qi = 0; qi < studentAnswers[mi].length; qi++) {
        allStudentAnswers.push(studentAnswers[mi][qi]);
      }
    }
    try {
      const completeDate = new Date();
      const response = await axios.post('https://www.tenaflyprepacademy.com/server/digitalSAT/test/grade', {
        answers: allStudentAnswers.join(','),
        JWT: localStorage.getItem('user'),
        test_id: testId,
        date: `${completeDate.getMonth() + 1}/${completeDate.getDate()}/${completeDate.getFullYear()}`,
        leave_count: countLeaves,
        RWMod1: studentAnswers[0].join('~'),
        RWMod2: studentAnswers[1].join('~'),
        MathMod1: studentAnswers[2].join('~'),
        MathMod2: studentAnswers[3].join('~'),
        PSAT: testId.includes('PSAT') ? 'True' : 'False',
      });
      if (response.data.error_code === 10) alert('Test not found in database.');
      else if (response.data.error_code === 7) alert('You are not logged in to take this test.');
      else if (response.data.error_code === 5) alert('No answers were submitted for grading.');
      else if (response.data.error_code === -1) {
        console.log(response.data);
        return true;
      }
      return false;
    } catch (err) {
      console.log(err);
      return false;
    }
  };

  const changeFRQAnswer = (e) => {
    const newAnswers = [...studentAnswers];
    newAnswers[mIndex][qIndex] = e.target.value.replace(/,/g, '');
    setStudentAnswers(newAnswers);
  };

  const inc = () => {
    if (qIndex < test[mIndex].length - 1) setQIndex(qIndex + 1);
  };

  const dec = () => {
    if (qIndex > 0) setQIndex(qIndex - 1);
  };

  const submitModule = () => {
    setOpenConfirm(true);
  };

  const confirmSubmitModule = async () => {
    if (mIndex === 3) {
      const submitted = await submitForGrading();
      if (submitted) {
        alert('Congrats! You have finished your practice Digital SAT. \nYou may now log into your account to see your scores.');
        window.location.href = '/Login';
      } else {
        alert('There seems to be a problem with connecting to our server.\nPlease try again in a minute.');
      }
    } else {
      setMIndex(mIndex + 1);
      setQIndex(0);
      setShowInstructions(true);
    }
  };

  function flagQuestion(m, q) {
    const updatedFlaggedQuestions = [...flaggedQuestions];
    updatedFlaggedQuestions[m][q] = !updatedFlaggedQuestions[m][q];
    setFlaggedQuestions(updatedFlaggedQuestions);
  }

  const getQuestionStatus = (index) => {
    if (flaggedQuestions[mIndex][index]) return 'flagged';
    if (studentAnswers[mIndex][index]) return 'answered';
    return 'unanswered';
  };

  // Login screen
  if (!hideStartButton) {
    return (
      <div style={{ height: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', backgroundColor: '#f9fafb' }}>
        <div style={{ width: '100%', maxWidth: '28rem', padding: '2rem', backgroundColor: 'white', borderRadius: '0.5rem', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }}>
          <h1 style={{ marginBottom: '0.5rem', textAlign: 'center' }}>Tenafly Prep Academy's</h1>
          <h2 style={{ marginBottom: '2rem', textAlign: 'center' }}>Digital Testing Platform</h2>
          {isLoggedIn ? (
            <form onSubmit={getTest} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <div>
                <label htmlFor="secret-input" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Test secret code:
                </label>
                <input
                  id="secret-input"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                  style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                />
              </div>
              <button type="submit" style={{ width: '100%', padding: '0.5rem 1rem', backgroundColor: '#030213', color: 'white', borderRadius: '0.375rem', cursor: 'pointer', border: 'none' }}>
                Get Test
              </button>
            </form>
          ) : (
            <form onSubmit={login} style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              <p style={{ color: '#d4183d', textAlign: 'center' }}>Login using your account credentials</p>
              <div>
                <label htmlFor="email" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Email:
                </label>
                <input id="email" type="email" onChange={(e) => setEmail(e.target.value)} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
              </div>
              <div>
                <label htmlFor="password" style={{ display: 'block', marginBottom: '0.5rem' }}>
                  Password:
                </label>
                <input id="password" type="password" onChange={(e) => setPassword(e.target.value)} style={{ width: '100%', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }} />
              </div>
              <button type="submit" style={{ width: '100%', padding: '0.5rem 1rem', backgroundColor: '#030213', color: 'white', borderRadius: '0.375rem', cursor: 'pointer', border: 'none' }}>
                Login
              </button>
            </form>
          )}
        </div>
      </div>
    );
  }

  // Instructions screen
  if (showInstructions) {
    return (
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'white' }}>
        <div style={{ height: '3.5rem', borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem' }}>
          <div style={{ padding: '0.375rem 0.75rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '0.25rem' }}>
            <span style={{ fontSize: '0.875rem' }}>{formatTime(timeRemaining)}</span>
          </div>
          <div style={{ fontSize: '0.875rem' }}>
            {mIndex < 2 ? 'Reading/Writing' : 'Math'} Module {(mIndex % 2) + 1}
          </div>
          {userName && <div style={{ fontSize: '0.875rem' }}>{userName}</div>}
        </div>

        <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '2rem' }}>
          <div style={{ maxWidth: '42rem', textAlign: 'center' }}>
            <h1 style={{ marginBottom: '1.5rem' }}>{mIndex < 2 ? 'Reading/Writing' : 'Math'} Module {(mIndex % 2) + 1}</h1>
            <button
              onClick={() => setShowInstructions(false)}
              style={{ padding: '0.75rem 1.5rem', backgroundColor: '#030213', color: 'white', borderRadius: '0.375rem', cursor: 'pointer', border: 'none', fontSize: '1.125rem' }}
            >
              Start {mIndex < 2 ? 'Reading/Writing' : 'Math'}
            </button>
            <div style={{ marginTop: '2rem', textAlign: 'left' }}>
              <p style={{ marginBottom: '1rem' }}>Testing Tools and Features:</p>
              <img src="testfeatures.png" style={{ width: '100%', maxWidth: '42rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '0.375rem' }} alt="Test features" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Main test interface
  if (qIndex === -1 || mIndex === -1) return null;

  const currentQuestion = test[mIndex][qIndex];

  return (
    <>
      {/* Formula Sheet Overlay */}
      {showFormulas && (
        <div
          onClick={() => setShowFormulas(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}
        >
          <img src="FormulaSheet.png" alt="formulas" style={{ maxWidth: '100%', maxHeight: '100%' }} />
        </div>
      )}

      {/* FRQ Instructions Overlay */}
      {showFRQInstructions && (
        <div
          onClick={() => setShowFRQInstructions(false)}
          style={{ position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' }}
        >
          <img src="FRQInstructions.png" alt="FRQ Instructions" style={{ maxWidth: '100%', maxHeight: '100%' }} />
        </div>
      )}

      {/* Main Interface */}
      <div style={{ height: '100vh', display: 'flex', flexDirection: 'column', backgroundColor: 'white', userSelect: 'none' }}>
        {/* Top Header */}
        <div style={{ height: '3.5rem', borderBottom: '1px solid rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 1.5rem', flexShrink: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
            <div style={{ padding: '0.375rem 0.75rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '0.25rem' }}>
              <span style={{ fontSize: '0.875rem' }}>{formatTime(timeRemaining)}</span>
            </div>
          </div>

          <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <span style={{ fontSize: '0.875rem', color: '#717182' }}>{mIndex < 2 ? 'Reading/Writing' : 'Math'}</span>
            <span style={{ fontSize: '0.875rem' }}>(Module {(mIndex % 2) + 1} of 2)</span>
          </div>

          {userName && <div style={{ fontSize: '0.875rem' }}>{userName}</div>}
        </div>

        {/* Main Content Area */}
        <div ref={containerRef} style={{ flex: 1, display: 'flex', overflow: 'hidden', position: 'relative' }}>
          {/* Left Panel - Question Image */}
          <div style={{ width: `${leftPanelWidth}%`, height: '100%', borderRight: '1px solid rgba(0,0,0,0.1)', overflow: 'auto', padding: '1.5rem', backgroundColor: '#f9fafb' }}>
            <div style={{ position: 'relative' }}>
              <canvas
                onMouseDown={startDrawing}
                onTouchStart={startDrawing}
                onMouseUp={finishDrawing}
                onTouchEnd={finishDrawing}
                onMouseMove={draw}
                onTouchMove={draw}
                onMouseOut={finishDrawing}
                onTouchCancel={finishDrawing}
                ref={canvasRef}
                style={{ position: 'absolute', top: 0, left: 0, zIndex: 10, touchAction: 'none', backgroundColor: 'transparent' }}
              />
              <img src={currentQuestion.url} alt="Question" onLoad={changeCanvas} ref={imageRef} style={{ width: '100%', display: 'block' }} />
            </div>
            <div style={{ marginTop: '1rem', display: 'flex', gap: '0.5rem' }}>
              <button onClick={clearCanvas} style={{ padding: '0.375rem 0.75rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '0.375rem', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem' }}>
                Clear Drawings
              </button>
              {mIndex > 1 && (
                <button onClick={() => setShowFormulas(true)} style={{ padding: '0.375rem 0.75rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '0.375rem', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem' }}>
                  Show Formulas
                </button>
              )}
            </div>
          </div>

          {/* Resize Handle */}
          <div
            onMouseDown={() => setIsResizing(true)}
            style={{
              width: '4px',
              cursor: 'col-resize',
              backgroundColor: 'transparent',
              position: 'absolute',
              top: 0,
              bottom: 0,
              left: `${leftPanelWidth}%`,
              zIndex: 20,
              transform: 'translateX(-2px)',
            }}
            onMouseEnter={(e) => (e.target.style.backgroundColor = 'rgba(59, 130, 246, 0.2)')}
            onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
          />

          {/* Right Panel - Answer Selection & Navigation */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            {/* Top Section - Answer Selection */}
            <div style={{ flex: 1, borderBottom: '1px solid rgba(0,0,0,0.1)', overflow: 'auto' }}>
              <div style={{ padding: '1.5rem' }}>
                {/* Question Header */}
                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1.5rem', paddingBottom: '1rem', borderBottom: '1px solid rgba(0,0,0,0.1)' }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem' }}>
                    <span style={{ fontSize: '0.875rem', color: '#717182' }}>
                      Question {qIndex + 1} of {test[mIndex].length}
                    </span>
                  </div>
                  <button
                    onClick={() => flagQuestion(mIndex, qIndex)}
                    style={{
                      padding: '0.375rem 0.75rem',
                      border: flaggedQuestions[mIndex][qIndex] ? 'none' : '1px solid rgba(0,0,0,0.1)',
                      borderRadius: '0.375rem',
                      backgroundColor: flaggedQuestions[mIndex][qIndex] ? '#030213' : 'white',
                      color: flaggedQuestions[mIndex][qIndex] ? 'white' : '#030213',
                      cursor: 'pointer',
                      fontSize: '0.875rem',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                    }}
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill={flaggedQuestions[mIndex][qIndex] ? 'currentColor' : 'none'} stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ width: '1rem', height: '1rem' }}>
                      <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
                      <line x1="4" x2="4" y1="22" y2="15" />
                    </svg>
                    {flaggedQuestions[mIndex][qIndex] ? 'Flagged' : 'Flag'}
                  </button>
                </div>

                {/* Answer Selection */}
                <h3 style={{ marginBottom: '1rem' }}>Your Answer</h3>

                {currentQuestion.FRQ ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                    <div>
                      <label htmlFor={`${mIndex}-${qIndex}`} style={{ display: 'block', marginBottom: '0.5rem' }}>
                        Type your answer:
                      </label>
                      <input
                        id={`${mIndex}-${qIndex}`}
                        value={studentAnswers[mIndex][qIndex]}
                        onChange={changeFRQAnswer}
                        maxLength={5}
                        style={{ maxWidth: '20rem', padding: '0.5rem', border: '1px solid #d1d5db', borderRadius: '0.375rem' }}
                      />
                    </div>
                    <p style={{ fontSize: '0.875rem', color: '#d4183d' }}>Please read the FRQ instructions carefully to avoid mistakes in grading.</p>
                    <button onClick={() => setShowFRQInstructions(true)} style={{ padding: '0.375rem 0.75rem', border: '1px solid rgba(0,0,0,0.1)', borderRadius: '0.375rem', backgroundColor: 'white', cursor: 'pointer', fontSize: '0.875rem', width: 'fit-content' }}>
                      Show FRQ Instructions
                    </button>
                  </div>
                ) : (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem', marginBottom: '2rem' }}>
                    {['A', 'B', 'C', 'D'].map((answer, i) => {
                      const isSelected = studentAnswers[mIndex][qIndex] === answer;
                      return (
                        <div
                          key={`${mIndex}-${qIndex}-${i}`}
                          onClick={() => {
                            const newAnswers = [...studentAnswers];
                            newAnswers[mIndex][qIndex] = answer;
                            setStudentAnswers(newAnswers);
                          }}
                          style={{
                            padding: '1rem',
                            border: isSelected ? '2px solid #3b82f6' : '2px solid #e5e7eb',
                            borderRadius: '0.5rem',
                            cursor: 'pointer',
                            backgroundColor: isSelected ? '#eff6ff' : 'white',
                            transition: 'all 0.2s',
                          }}
                          onMouseEnter={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = '#f9fafb';
                              e.currentTarget.style.borderColor = '#d1d5db';
                            }
                          }}
                          onMouseLeave={(e) => {
                            if (!isSelected) {
                              e.currentTarget.style.backgroundColor = 'white';
                              e.currentTarget.style.borderColor = '#e5e7eb';
                            }
                          }}
                        >
                          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
                            <div
                              style={{
                                width: '1.25rem',
                                height: '1.25rem',
                                borderRadius: '50%',
                                border: isSelected ? '2px solid #3b82f6' : '2px solid #9ca3af',
                                backgroundColor: isSelected ? '#3b82f6' : 'transparent',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                marginTop: '0.125rem',
                              }}
                            >
                              {isSelected && <div style={{ width: '0.5rem', height: '0.5rem', borderRadius: '50%', backgroundColor: 'white' }} />}
                            </div>
                            <div style={{ flex: 1 }}>
                              <span style={{ textTransform: 'uppercase' }}>{answer}</span>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Navigation Buttons */}
                <div style={{ display: 'flex', gap: '0.75rem', paddingTop: '1.5rem', borderTop: '1px solid rgba(0,0,0,0.1)', marginTop: '1.5rem' }}>
                  <button
                    onClick={dec}
                    disabled={qIndex === 0}
                    style={{
                      padding: '0.5rem 1rem',
                      border: '1px solid rgba(0,0,0,0.1)',
                      borderRadius: '0.375rem',
                      backgroundColor: 'white',
                      cursor: qIndex === 0 ? 'not-allowed' : 'pointer',
                      opacity: qIndex === 0 ? 0.5 : 1,
                    }}
                  >
                    Previous
                  </button>
                  {qIndex < test[mIndex].length - 1 ? (
                    <button onClick={inc} style={{ padding: '0.5rem 1rem', backgroundColor: '#030213', color: 'white', borderRadius: '0.375rem', cursor: 'pointer', border: 'none' }}>
                      Next
                    </button>
                  ) : (
                    <button onClick={submitModule} style={{ padding: '0.5rem 1rem', backgroundColor: '#030213', color: 'white', borderRadius: '0.375rem', cursor: 'pointer', border: 'none' }}>
                      Submit Module
                    </button>
                  )}
                </div>

                {/* Confirm Submit */}
                {openConfirm && (
                  <div style={{ marginTop: '1.5rem', padding: '1rem', border: '2px solid #d4183d', borderRadius: '0.5rem', backgroundColor: 'rgba(212, 24, 61, 0.05)' }}>
                    <p style={{ color: '#d4183d', marginBottom: '0.5rem' }}>Are you sure you want to submit?</p>
                    <p style={{ color: '#d4183d', marginBottom: '1rem' }}>You will not be allowed to come back to this module later.</p>
                    <button onClick={confirmSubmitModule} style={{ padding: '0.5rem 1rem', backgroundColor: '#d4183d', color: 'white', borderRadius: '0.375rem', cursor: 'pointer', border: 'none' }}>
                      Confirm Submit
                    </button>
                  </div>
                )}

                <div style={{ marginTop: '1.5rem', fontSize: '0.875rem', color: '#d4183d' }}>Text (201)-471-8366 if you encounter any bugs during test taking.</div>
              </div>
            </div>

            {/* Bottom Section - Question Navigation (Collapsable) */}
            {showNavigation ? (
              <div style={{ height: '20rem', borderTop: '1px solid rgba(0,0,0,0.1)', overflow: 'auto', flexShrink: 0 }}>
                <div style={{ padding: '1.5rem' }}>
                  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '1rem' }}>
                    <h3>Question Navigation</h3>
                    <button onClick={() => setShowNavigation(false)} style={{ padding: '0.25rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer' }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="m6 9 6 6 6-6" />
                      </svg>
                    </button>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(8, 1fr)', gap: '0.5rem', marginBottom: '1.5rem' }}>
                    {test[mIndex].map((question, index) => {
                      const status = getQuestionStatus(index);
                      return (
                        <button
                          key={index}
                          onClick={() => setQIndex(index)}
                          style={{
                            aspectRatio: '1',
                            borderRadius: '0.5rem',
                            border: status === 'answered' ? '2px solid #3b82f6' : status === 'flagged' ? '2px solid #eab308' : '2px solid #d1d5db',
                            backgroundColor: status === 'answered' ? '#3b82f6' : status === 'flagged' ? '#fef3c7' : 'white',
                            color: status === 'answered' ? 'white' : status === 'flagged' ? '#92400e' : '#030213',
                            cursor: 'pointer',
                            fontSize: '0.875rem',
                            outline: qIndex === index ? '2px solid #3b82f6' : 'none',
                            outlineOffset: '2px',
                          }}
                        >
                          {index + 1}
                        </button>
                      );
                    })}
                  </div>

                  <div style={{ display: 'flex', gap: '1.5rem', fontSize: '0.875rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: '0.25rem', border: '2px solid #d1d5db', backgroundColor: 'white' }} />
                      <span>Unanswered</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: '0.25rem', backgroundColor: '#3b82f6' }} />
                      <span>Answered</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <div style={{ width: '1.5rem', height: '1.5rem', borderRadius: '0.25rem', backgroundColor: '#fef3c7', border: '2px solid #eab308' }} />
                      <span>Flagged</span>
                    </div>
                  </div>
                </div>
              </div>
            ) : (
              <div style={{ flexShrink: 0, borderTop: '1px solid rgba(0,0,0,0.1)', backgroundColor: '#f9fafb' }}>
                <button onClick={() => setShowNavigation(true)} style={{ width: '100%', height: '2.5rem', border: 'none', backgroundColor: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem' }}>
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="m18 15-6-6-6 6" />
                  </svg>
                  Show Question Navigation
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}
